import React from 'react'
import { connect } from 'react-redux'
import {
  BasketAllocation,
  CollateralAllocation,
  CollateralAllocationDetail,
  Direction,
  EntityType,
  HaircutType,
  ProductSector,
  RepoType,
  TradingType,
  UserPreference,
  ValidationIssue
} from 'api'
import { iconSet } from 'view/components/icons'
import {
  Allocated,
  AllocatedValue,
  AllocationTable,
  FillOrTrimButton,
  FillOrTrimContainer,
  HaircutTypeWrapper,
  Heading,
  NoEntities,
  Remaining,
  RemoveAllocationButton,
  Required,
  ScrollContainer
} from '../allocationDialogStyles'
import { ValidationIssuesContainer } from 'view/shared/error'
import { NumericInput } from 'view/components/inputs'
import { TradingTypeHelper } from 'model'
import { Locale, Translation } from 'locale'
import { createStructuredSelector } from 'reselect'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { isNumber } from 'lodash-es'
import { getHaircutFormatOptions, getNotionalFormatOptions } from 'view/shared/datagrid'
import { HaircutTypeSelectboxField } from 'view/shared/entity/haircutOptions/HaircutTypeSelectbox'
import { bondPriceMaxFieldLength, bondPriceMaxFractionDigits, LabelPosition } from 'view/styles/constants'
import { BooleanSelectbox } from 'view/shared/entity'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { startConsiderationOverrideEnabledSelector } from 'selectors/featureSelectors'

interface AllocationPanelProps {
  totalAmountRequired: number
  allocationDetails: CollateralAllocationDetail[]
  basketAllocation: BasketAllocation
  tradingType: TradingType
  ownOrder: boolean
  direction: Direction
  repoType: RepoType
  userPreference: UserPreference
  clipboardIssues: ValidationIssue[]
  onCollateralAllocationChange: (
    originalCollateralAllocation: CollateralAllocation,
    updatedPartialAllocation: Partial<CollateralAllocation> | undefined
  ) => unknown
  onHaircutTypeChange: (haircutType: HaircutType | null) => unknown
  onSetTrimOrFill: (index: number) => unknown
  onSetCalculatingTradingType: (tradingType: TradingType) => unknown
  startConsiderationOverrideEnabled: boolean
  productSectorSelector: (productSectorId: string) => ProductSector | null
}

const mapStateToProps = createStructuredSelector({
  userPreference: createUserPreferenceSelector(),
  startConsiderationOverrideEnabled: startConsiderationOverrideEnabledSelector,
  productSectorSelector: createEntityGetterSelector(EntityType.PRODUCT_SECTOR)
})

export const AllocationsPanelComponent: React.FunctionComponent<AllocationPanelProps> = props => {
  const {
    totalAmountRequired,
    allocationDetails,
    userPreference,
    basketAllocation,
    tradingType,
    ownOrder,
    direction,
    repoType,
    clipboardIssues,
    onCollateralAllocationChange,
    onHaircutTypeChange,
    onSetTrimOrFill,
    onSetCalculatingTradingType,
    startConsiderationOverrideEnabled,
    productSectorSelector
  } = props
  const { remainingAllocationRequired, amountAllocated, haircutType, validationIssues } = basketAllocation

  const availableAllocationDetails = allocationDetails
    .map((allocationDetail, index) => {
      return { ...allocationDetail, index }
    })
    .filter(allocationDetail => allocationDetail.collateralAllocation.selected)
  const fullyAllocated = remainingAllocationRequired === 0
  const overAllocated = isNumber(remainingAllocationRequired) && remainingAllocationRequired < 0
  const generalValidationIssues = (validationIssues ?? [])
    .filter(validationIssue => validationIssue.fieldName !== 'amountAllocated')
    .concat(clipboardIssues)
  const notionalFormatOptions = getNotionalFormatOptions(userPreference.amountFormat, false)
  const allocationFormatOptions = tradingType === TradingType.CASH ? { minDp: 2 } : getNotionalFormatOptions(userPreference.amountFormat)
  const amountFormatOptions = tradingType === TradingType.CASH ? { amountFormat: 'other' } : { amountFormat: userPreference.amountFormat }
  const repoTypeStockBorrowingLending = repoType === RepoType.STOCK_BORROWING_LENDING

  return (
    <>
      <Required fullyAllocated={fullyAllocated}>
        <span>
          {tradingType && TradingTypeHelper.getName(tradingType)}{' '}
          {Locale.translate(Translation.TC_ALLOCATION_REQUIRED, amountFormatOptions)}
        </span>
        <span>{Locale.formatNumber(totalAmountRequired, allocationFormatOptions)}</span>
      </Required>
      <Allocated fullyAllocated={fullyAllocated}>
        <span>{Locale.translate(Translation.TC_AMOUNT_ALLOCATED, amountFormatOptions)}</span>
        <AllocatedValue fullyAllocated={fullyAllocated}>
          {Locale.formatNumber(amountAllocated || 0, allocationFormatOptions)}
        </AllocatedValue>
      </Allocated>
      <Remaining overAllocated={overAllocated}>
        <span>{Locale.translate(Translation.TC_REMAINING_ALLOCATION_REQUIRED, amountFormatOptions)}</span>
        <span>{Locale.formatNumber(remainingAllocationRequired || 0, allocationFormatOptions)}</span>
      </Remaining>
      <HaircutTypeWrapper>
        <HaircutTypeSelectboxField
          fieldName="haircutType"
          label={Locale.translate(Translation.TC_HAIRCUT_TYPE)}
          labelPosition={LabelPosition.Left}
          validationIssues={generalValidationIssues}
          value={haircutType}
          onUpdate={(value: HaircutType | null) => onHaircutTypeChange(value)}
          dataTestId="AllocationPanel-HaircutTypeSelectboxField"
          readOnly={repoTypeStockBorrowingLending}
        />
      </HaircutTypeWrapper>
      <Heading>
        <span>{Locale.translate(Translation.TC_ALLOCATED_PRODUCTS, { productCount: availableAllocationDetails.length })}:</span>
        {generalValidationIssues && (
          <ValidationIssuesContainer data-test-id={'CollateralBasketAllocationDialog-ValidationIssuesContainer'}>
            {generalValidationIssues
              .filter(validationIssue => validationIssue.fieldName !== 'haircutType')
              .map((validationIssue, index) => (
                <li key={index}>{validationIssue.errorMessage}</li>
              ))}
          </ValidationIssuesContainer>
        )}
      </Heading>
      {availableAllocationDetails.length > 0 ? (
        <ScrollContainer maxHeight={350} maxWidth={850}>
          <AllocationTable>
            <thead>
              <tr>
                <th>{Locale.translate(Translation.TC_ISIN_CUSIP)}</th>
                <th>{Locale.translate(Translation.TC_COLLATERAL)}</th>
                <th>{Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat })}</th>
                <th>{Locale.translate(Translation.TC_HAIRCUT)}</th>
                <th>{Locale.translate(Translation.TC_ABBR_ALL_IN_PRICE)}</th>
                <th>{Locale.translate(Translation.TC_FACTOR)}</th>
                <th>{Locale.translate(Translation.TC_ABBR_COLLATERAL_ACCRUED_INTEREST)}</th>
                <th>{Locale.translate(Translation.TC_ABBR_START_CONSIDERATION)}</th>
                {startConsiderationOverrideEnabled && <th>{Locale.translate(Translation.TC_ABBR_START_CONSIDERATION_OVERRIDE)}</th>}
                <th>{Locale.translate(Translation.TC_ABBR_END_CONSIDERATION)}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {availableAllocationDetails.map(allocationDetail => {
                const {
                  collateral: { name, id, productSectorId },
                  collateralAllocation: {
                    factor,
                    notional,
                    haircut,
                    accruedInterest,
                    startConsideration,
                    allInPrice,
                    endConsideration,
                    startConsiderationOverride
                  },
                  fieldValidationIssues,
                  index
                } = allocationDetail

                const onAllocationChange = (value: Partial<CollateralAllocation> | undefined) => {
                  onCollateralAllocationChange(allocationDetail.collateralAllocation, value)
                }

                const productSector = productSectorSelector(productSectorId)

                return (
                  <tr data-test-id={'AllocationPanel-AllocationRow'} key={index}>
                    <td style={{ minWidth: 120 }}>{id}</td>
                    <td style={{ minWidth: 150 }}>{name}</td>
                    <td style={{ minWidth: 120 }} onFocus={() => onSetCalculatingTradingType(TradingType.NOTIONAL)}>
                      <FillOrTrimContainer>
                        <NumericInput
                          dataTestId={'AllocationPanel-NumericInput-Notional'}
                          value={notional}
                          onUpdate={value => onAllocationChange({ notional: value })}
                          validationIssues={fieldValidationIssues.notional}
                          formatOptions={notionalFormatOptions}
                        />
                        {tradingType === TradingType.NOTIONAL && (
                          <FillOrTrimButton
                            dataTestId={'AllocationPanel-FillOrTrimButton-Notional'}
                            disabled={fullyAllocated}
                            onClick={() => onSetTrimOrFill(index)}>
                            {iconSet.fillTrimIcon}
                          </FillOrTrimButton>
                        )}
                      </FillOrTrimContainer>
                    </td>
                    <td style={{ minWidth: 80 }}>
                      <NumericInput
                        dataTestId={'AllocationPanel-NumericInput-Haircut'}
                        value={haircut}
                        validationIssues={fieldValidationIssues.haircut}
                        formatOptions={getHaircutFormatOptions(false, userPreference.haircutSign, ownOrder, direction)}
                        onUpdate={value =>
                          onAllocationChange({
                            haircut: value,
                            allInPrice: null
                          })
                        }
                        readOnly={repoTypeStockBorrowingLending}
                      />
                    </td>
                    <td style={{ minWidth: 80 }}>
                      {repoType === RepoType.CLASSIC ? (
                        <NumericInput
                          dataTestId={'AllocationPanel-NumericInput-AllInPrice'}
                          value={allInPrice}
                          onUpdate={value => onAllocationChange({ allInPrice: value })}
                          validationIssues={fieldValidationIssues.allInPrice}
                          maxLength={bondPriceMaxFieldLength}
                          formatOptions={{
                            maxFractionDigits: bondPriceMaxFractionDigits
                          }}
                        />
                      ) : (
                        allInPrice
                      )}
                    </td>
                    <td style={{ minWidth: 80 }}>{factor}</td>
                    <td style={{ minWidth: 60 }}>{Locale.formatNumber(accruedInterest, { minDp: 2 })}</td>
                    <td style={{ minWidth: 100 }} onFocus={() => onSetCalculatingTradingType(TradingType.CASH)}>
                      <FillOrTrimContainer>
                        <NumericInput
                          dataTestId={'AllocationPanel-NumericInput-StartConsideration'}
                          value={startConsideration}
                          onUpdate={value => onAllocationChange({ startConsideration: value })}
                          validationIssues={fieldValidationIssues.startConsideration}
                          formatOptions={{ minDp: 2 }}
                        />
                        {tradingType === TradingType.CASH && (
                          <FillOrTrimButton
                            dataTestId={'AllocationPanel-FillOrTrimButton-Cash'}
                            disabled={fullyAllocated}
                            onClick={() => onSetTrimOrFill(index)}>
                            {iconSet.fillTrimIcon}
                          </FillOrTrimButton>
                        )}
                      </FillOrTrimContainer>
                    </td>
                    {startConsiderationOverrideEnabled && (
                      <td style={{ minWidth: 70 }}>
                        <BooleanSelectbox
                          dataTestId={'AllocationPanel-NumericInput-StartConsideration'}
                          value={startConsiderationOverride}
                          disabled={productSector?.startConsiderationOverrideThreshold === 0 || tradingType === TradingType.NOTIONAL}
                          onUpdate={value =>
                            onAllocationChange({
                              startConsiderationOverride: value,
                              startConsideration: null
                            })
                          }
                          validationIssues={fieldValidationIssues.startConsiderationOverride}
                        />
                      </td>
                    )}

                    <td style={{ minWidth: 100 }}>{Locale.formatNumber(endConsideration, { minDp: 2 })}</td>
                    <td>
                      <RemoveAllocationButton
                        dataTestId={'AllocationPanel-RemoveAllocationButton'}
                        onClick={() => onAllocationChange(undefined)}>
                        {iconSet.crossIcon}
                      </RemoveAllocationButton>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </AllocationTable>
        </ScrollContainer>
      ) : (
        <NoEntities>{Locale.translate(Translation.SC_SELECT_ALLOCATIONS)}</NoEntities>
      )}
    </>
  )
}

export const AllocationsPanel = connect(mapStateToProps)(AllocationsPanelComponent)
